import type { Quat, Vec3 } from './assemblyPose';

// THE SIX SIDES OF A PART, AND WHICH WAY EACH ONE POINTS.
//
// One vocabulary, in one place, because four things now say it: the box the assembly phase
// writes on a mesh (meshAnnotations.FACES), the dialog that turns a mesh by naming two of
// them (MeshRotateModal), the gestures a placing can be (gestures.ts), and the block Phase 5
// is told about the parts it is asked about. Four copies of six words is four chances to
// disagree about which way "back" points, and the way that fails is silent: a part turned to
// a face name that means something else here than it did there.
//
// NAMED IN THE ASSEMBLY'S FRAME AND NOT THE PART'S. Nothing in this codebase knows which side
// of a chair leg its maker calls the front. What it knows is the frame every number in the
// assembly phase is written in — x across, y into the picture, z up — and these six names are
// that frame's own directions, fixed once against the view the viewer opens on: −y faces the
// camera at home and is therefore the front, +z is up and is therefore the top. Which is the
// frame a mesh is re-authored into once its orientation is settled (see mesh_orient.py), so
// the words are true of the part rather than of the picture it was reconstructed from.
//
// The Python twin is mesh_orient.FACES, and the two are checked against each other by the
// wire: the turn dialog sends these names and the server looks them up in that table.
export type Face = 'front' | 'back' | 'left' | 'right' | 'top' | 'bottom';

export const FACES: Face[] = ['front', 'back', 'left', 'right', 'top', 'bottom'];

export const OPPOSITE: Record<Face, Face> = {
  front: 'back', back: 'front', left: 'right', right: 'left', top: 'bottom', bottom: 'top',
};

// Which way each one faces, as a unit vector of the frame above.
export const FACE_DIR: Record<Face, Vec3> = {
  right: [1, 0, 0], left: [-1, 0, 0],
  back: [0, 1, 0], front: [0, -1, 0],
  top: [0, 0, 1], bottom: [0, 0, -1],
};

// The i18n key each one is written under. The six words are translated in one place — the
// assembly phase's, where they are drawn on the box — and every other reader looks them up
// rather than keeping a second set.
export const faceKey = (face: Face) =>
  `notice.assembly.face${face.charAt(0).toUpperCase()}${face.slice(1)}`;

// WHICH WAY A MIRROR PAIR'S FACE LOOKS: out into the room, or in towards the other unit.
//
// Only the two faces the mirror moves have an answer. A left/right pair is reflected across x
// and nothing else, so front, back, top and bottom are the same face on both units and neither
// inner nor outer means anything about them — null, not a guess.
//
// THE RULE IS THE FACE CONVENTION RUN FORWARD, not a second one. Faces are named in the
// assembly's frame from the viewer standing in front of the product (see FACE_DIR above), so
// +x is the viewer's right and the LEFT unit of a pair sits at −x with the rest of the
// assembly on its +x side. Its right face therefore looks in and its left face looks out. The
// right unit is that unit mirrored, so it reads the other way about — which is the whole
// reason this is worth a function: "outer" is NOT a stable face name across a pair, and a rule
// written as "the hole goes on the left face" is true of one of the two objects only.
export type FaceSide = 'inner' | 'outer';

export const pairFaceSide = (face: Face, hand: 'left' | 'right'): FaceSide | null => {
  if (face !== 'left' && face !== 'right') return null;
  return face === hand ? 'outer' : 'inner';
};

// The four faces at right angles to this one: everything except it and the one opposite,
// which are the two ends of a single axis. Nothing chosen yet, nothing struck off.
export const perpendicular = (chosen: Face | '') =>
  (chosen ? FACES.filter((f) => f !== chosen && f !== OPPOSITE[chosen]) : FACES);

// Where the part's own faces MUST END UP, said as two of them. See quatOfFaces.
export type FacePair = { face: Face; to: Face };

const cross = (a: Vec3, b: Vec3): Vec3 => [
  a[1] * b[2] - a[2] * b[1],
  a[2] * b[0] - a[0] * b[2],
  a[0] * b[1] - a[1] * b[0],
];

const dot = (a: Vec3, b: Vec3) => a[0] * b[0] + a[1] * b[1] + a[2] * b[2];

// THE TURN THAT CARRIES TWO NAMED FACES ONTO TWO NAMED DIRECTIONS, as a quaternion — or null
// when the pair describes no rotation at all.
//
// The twin of mesh_orient.rotation_for on the server, and the same argument for it: ANY TWO
// WILL DO as long as they are at right angles, and two that are settle the whole orientation,
// because two perpendicular unit axes and their cross product are a basis. Perpendicular is
// checked on BOTH sides rather than assumed — two faces of one axis describe nothing, and one
// face told to be two different directions contradicts itself — and the only honest answer to
// either is none.
//
// Exact, not fitted: every entry of the matrix is 0 or ±1, so the quaternion comes out of it
// without a square root worth worrying about. Shepperd's method, taking the root off whichever
// diagonal term is largest, because the naive w-first form divides by nearly zero for a half
// turn — which is the commonest turn there is.
export function quatOfFaces(pairs: readonly FacePair[]): Quat | null {
  if (pairs.length !== 2) return null;
  const [a, b] = pairs.map((p) => FACE_DIR[p.face]);
  const [ta, tb] = pairs.map((p) => FACE_DIR[p.to]);
  if (dot(a, b) || dot(ta, tb)) return null;

  // R = target · source⁻¹, and source is orthonormal, so its inverse is its transpose —
  // which is what makes this nine products and not a solve.
  const src = [a, b, cross(a, b)];
  const dst = [ta, tb, cross(ta, tb)];
  const m = [0, 1, 2].map((row) => [0, 1, 2].map(
    (col) => src.reduce((sum, _, k) => sum + dst[k][row] * src[k][col], 0),
  ));

  const trace = m[0][0] + m[1][1] + m[2][2];
  let q: number[];
  if (trace > 0) {
    const s = Math.sqrt(trace + 1) * 2;
    q = [(m[2][1] - m[1][2]) / s, (m[0][2] - m[2][0]) / s, (m[1][0] - m[0][1]) / s, 0.25 * s];
  } else if (m[0][0] > m[1][1] && m[0][0] > m[2][2]) {
    const s = Math.sqrt(1 + m[0][0] - m[1][1] - m[2][2]) * 2;
    q = [0.25 * s, (m[0][1] + m[1][0]) / s, (m[0][2] + m[2][0]) / s, (m[2][1] - m[1][2]) / s];
  } else if (m[1][1] > m[2][2]) {
    const s = Math.sqrt(1 + m[1][1] - m[0][0] - m[2][2]) * 2;
    q = [(m[0][1] + m[1][0]) / s, 0.25 * s, (m[1][2] + m[2][1]) / s, (m[0][2] - m[2][0]) / s];
  } else {
    const s = Math.sqrt(1 + m[2][2] - m[0][0] - m[1][1]) * 2;
    q = [(m[0][2] + m[2][0]) / s, (m[1][2] + m[2][1]) / s, 0.25 * s, (m[1][0] - m[0][1]) / s];
  }
  // Rounded because every component is one of 0, ±0.5 and ±√½, and the arithmetic above
  // leaves the last bits of a float on some of them.
  const [x, y, z, w] = q.map((v) => Math.round(v * 1e12) / 1e12 + 0);
  return [x, y, z, w];
}
